import { z } from 'zod';

export const sortOrderSchema = z.enum(['newest', 'oldest']);
export type SortOrder = z.infer<typeof sortOrderSchema>;
export const photoSectionSchema = z.enum(['celebration', 'memories']);
export type PhotoSection = z.infer<typeof photoSectionSchema>;

export const photoSchema = z.object({
  id: z.string().min(1),
  originalName: z.string(),
  capturedAt: z.string().datetime(),
  cameraCapturedAt: z.string().datetime().optional(),
  uploadedAt: z.string().datetime(),
  caption: z.string().refine((value) => Array.from(value).length <= 240),
  contributorName: z.string().max(80).optional(),
  section: photoSectionSchema.optional(),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  mimeType: z.string(),
  status: z.enum(['published', 'deleted']),
  assets: z.object({
    thumbnail: z.string(),
    display: z.string(),
    original: z.string().nullable(),
  }),
  deletedAt: z.string().datetime().optional(),
  canBanUploader: z.boolean().optional(),
  canRemove: z.boolean().optional(),
});

export type Photo = z.infer<typeof photoSchema>;

export const manifestSchema = z.object({
  version: z.literal(1),
  albumId: z.string().uuid().optional(),
  revision: z.number().int().nonnegative(),
  // Explicit website removals survive permanent media cleanup; restores clear them.
  photoDeletions: z.record(z.string().uuid(), z.number().int().positive()).default({}),
  photos: z.array(photoSchema),
  idempotency: z.record(z.string(), z.string()),
  uploaders: z.record(z.string(), z.string()).default({}),
  uploadBans: z.record(z.string(), z.string().datetime()).default({}),
  uploadsPaused: z.boolean().default(false),
  pendingPurges: z.array(z.string().uuid()).default([]),
});

export type Manifest = z.infer<typeof manifestSchema>;

export const UPLOAD_BAN_MS = 15 * 60 * 1000;
export const UPLOAD_BAN_MESSAGE =
  'Your photo was removed because it is not appropriate for this event. Camera, Upload and Family access are temporarily unavailable for 15 minutes. If this happens again, further action will be taken to remove you from the event.';
export const UPLOAD_PAUSED_MESSAGE =
  'The family has paused photo contributions. Camera and Upload are unavailable, but you can still enjoy the album. Contributions can resume if the family reopens them.';
export type UploadAccess = { bannedUntil: string | null; serverNow: string; uploadsPaused: boolean };
export type UploadJob = {
  id: string;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  photo?: Photo;
};

export type PhotoPage = {
  items: Photo[];
  page: number;
  pageSize: number;
  total: number;
  albumTotal?: number;
  totalPages: number;
  sort: SortOrder;
  revision: number;
};

export function normalizeCaption(value: unknown): string {
  return Array.from(
    String(value ?? '')
      .replace(/[\u0000-\u001f\u007f]/g, ' ') // eslint-disable-line no-control-regex -- remove unsafe control characters
      .replace(/\s+/g, ' ')
      .trim(),
  )
    .slice(0, 240)
    .join('');
}

export function sortPhotos<
  T extends Pick<Photo, 'uploadedAt'> & Partial<Pick<Photo, 'cameraCapturedAt' | 'section'>>,
>(items: T[], order: SortOrder): T[] {
  return [...items].sort((a, b) => {
    const time = (photo: T) =>
      Date.parse(
        photo.section !== 'memories' ? photo.cameraCapturedAt || photo.uploadedAt : photo.uploadedAt,
      );
    const delta = time(a) - time(b) || Date.parse(a.uploadedAt) - Date.parse(b.uploadedAt);
    return order === 'oldest' ? delta : -delta;
  });
}
